/**
 * Custom Domain (BYOD) settings card.
 *
 * Pro-tier feature. Gated twice client-side: by the clubId allowlist
 * (BYOD_ALLOWED_CLUBS, dev-only, mirrors lambda/routes/domains.ts) and
 * by the user's plan. Server-side gates the same way (404 envelope from
 * lambda), these client gates are UX only.
 *
 * Phase 3 against the mock: status polls auto-flip pending → validating
 * → active over ~30s thanks to lambda/lib/byod-aws.ts. Phase 2 swaps the
 * mock for real ACM + CloudFront + KVS.
 *
 * Spec: docs/features/custom-domains.md
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { PLATFORM_DOMAIN } from '../platform';
import {
  ByodBillingStatus,
  CustomDomain,
  DomainStatus,
  addDomain,
  createByodCheckout,
  createByodPortal,
  deleteDomain,
  getByodBillingStatus,
  getDomainStatus,
  listDomains,
} from '../api/client';

function gbpFromPence(pence: number): string {
  const n = (pence || 0) / 100;
  return '£' + (Number.isInteger(n) ? n.toString() : n.toFixed(2));
}

// BYOD is gated by features.customDomain (operator-set in SuperAdmin) +
// plan === 'pro' (the locked card below catches non-Pro). The previous
// hardcoded clubId allowlist was removed once the rail proved out, the
// feature flag is the operator's per-club switch.
interface Props {
  clubId: string | null;
  plan: string | null;
}

const POLL_MS = 5000;

const STATUS_LABEL: Record<DomainStatus, string> = {
  pending: 'Preparing your domain…',
  validating: 'Verifying ownership and issuing SSL…',
  active: 'Live',
  failed: 'Setup failed',
  decommissioning: 'Live, scheduled to be removed',
};

const STATUS_HELP: Record<DomainStatus, string> = {
  pending: 'Setting up an SSL certificate. This takes a few seconds.',
  validating: 'Waiting for your DNS records to be detected. Usually under a minute. (In dev this completes after ~30s on the mock.)',
  active: 'Your custom domain is live and serving your website.',
  failed: 'DNS validation timed out. Please re-check the records and contact support.',
  decommissioning: 'Your domain is still live and serving your website. It will be removed on the date below unless you re-subscribe to Custom Domain.',
};

// Strip the trailing FQDN dot before showing to humans, registrars
// accept both forms but the dot confuses people and some paste it
// verbatim into a field that rejects it.
function cleanForDisplay(value: string): string {
  return value.endsWith('.') ? value.slice(0, -1) : value;
}

function CopyableField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const display = cleanForDisplay(value);
  return (
    <div className="byod-record">
      <div className="byod-record-label">{label}</div>
      <div className="byod-record-row">
        {/* Render as a readonly input rather than text in a <code> tag,
            prevents Safari data detectors, 1Password, and similar from
            auto-linking substrings like "www.example.com" inside the
            full validation hostname. */}
        <input
          type="text"
          readOnly
          className="byod-record-value"
          value={display}
          onClick={e => e.currentTarget.select()}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
        />
        <button
          type="button"
          className="byod-copy-btn"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(display);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch {
              // Clipboard API may not be available; the input is still
              // selectable so the user can copy manually.
            }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

export default function CustomDomainCard({ clubId, plan }: Props) {
  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [input, setInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [billing, setBilling] = useState<ByodBillingStatus | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const pollRef = useRef<number | null>(null);

  void clubId; // available from JWT, gating uses billing.canManageDomains.

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(async (domain: string) => {
    try {
      const res = await getDomainStatus(domain);
      setDomains(prev => prev.map(d => (d.domain === domain ? res.domain : d)));
      const status: DomainStatus = res.domain.status;
      if (status === 'active' || status === 'failed') stopPoll();
    } catch {
      // transient, keep polling
    }
  }, [stopPoll]);

  // Single bootstrap: fetch billing status AND domains. We always fetch
  // the domains list because clubs with no active sub can still own
  // in-grace decommissioning domains, hiding those would make it look
  // like the rail tore down already, which it hasn't (30-day grace).
  // The lambda allows a read-only list even without active billing for
  // this reason; only add/get/delete actions require Pro-or-active-sub.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [bs, list] = await Promise.all([
          getByodBillingStatus(),
          listDomains().catch(() => ({ domains: [], count: 0 })),
        ]);
        if (cancelled) return;
        setBilling(bs);
        setDomains(list.domains);
      } catch {
        // status fetch failed (feature off etc), leave billing null,
        // page-level gate in App.tsx already 404'd anything serious.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Show a brief banner if we just came back from Stripe Checkout.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('byod') === 'success') {
      setInfo('Subscription active, you can now add your custom domain below.');
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh billing in case Stripe webhook landed before we returned.
      getByodBillingStatus().then(setBilling).catch(() => {});
    } else if (params.get('byod') === 'cancelled') {
      setInfo('Checkout cancelled, no charge was made.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Auto-poll while any domain is still in a transitional state.
  useEffect(() => {
    const inFlight = domains.find(d => d.status === 'pending' || d.status === 'validating');
    if (!inFlight) {
      stopPoll();
      return;
    }
    stopPoll();
    pollRef.current = window.setInterval(() => pollOnce(inFlight.domain), POLL_MS);
    return stopPoll;
  }, [domains, pollOnce, stopPoll]);

  const startCheckout = async () => {
    setBillingLoading(true);
    setError('');
    try {
      const { url } = await createByodCheckout();
      if (url) window.location.href = url;
    } catch (err: any) {
      setError(err?.message || 'Could not start checkout.');
      setBillingLoading(false);
    }
  };

  const openBillingPortal = async () => {
    setBillingLoading(true);
    setError('');
    try {
      const { url } = await createByodPortal();
      if (url) window.location.href = url;
    } catch (err: any) {
      setError(err?.message || 'Could not open billing portal.');
      setBillingLoading(false);
    }
  };

  // Upsell CTA, shown when the feature flag is on but the club hasn't
  // paid yet. Free → £15 + £5/mo; Club → £3/mo; Pro never lands here.
  // If the club has an in-grace domain (decommissioning), we surface
  // that ABOVE the upsell so it's obvious the domain is still live and
  // re-subscribing will keep it alive, not just "subscribe to add a
  // new domain". Hidden after-grace domains never come back.
  if (loaded && billing && !billing.canManageDomains && !billing.included) {
    const monthly = gbpFromPence(billing.monthlyPence);
    const setup = billing.setupPence > 0 ? gbpFromPence(billing.setupPence) : null;
    const tierLabel = plan === 'club' ? 'Club' : 'Free';
    const inGrace = domains.filter(d => d.status === 'decommissioning');
    return (
      <div className="byod-pane">
        {error && <div className="error-message">{error}</div>}
        {info && <div className="success-message">{info}</div>}

        {inGrace.length > 0 && (
          <div className="byod-grace-banner">
            <strong>Your custom domain is still live.</strong>
            {inGrace.map(d => (
              <div key={d.domain} className="byod-grace-row">
                <span className="byod-grace-domain">{d.domain}</span>
                <span className="byod-grace-deadline">
                  scheduled to be removed on{' '}
                  {d.decommissionAt
                    ? new Date(d.decommissionAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                    : 'the end of your grace period'}
                </span>
              </div>
            ))}
            <p className="byod-grace-hint">
              Subscribe below to keep it serving. If you don't, it detaches
              automatically on the date shown, your{' '}
              <em>yourclub.{PLATFORM_DOMAIN}</em> address keeps working
              either way.
            </p>
          </div>
        )}

        <div className="byod-upsell">
          <div className="byod-empty-icon" aria-hidden>🌐</div>
          <h2 className="byod-upsell-title">
            {inGrace.length > 0 ? 'Keep your custom domain' : 'Use your own domain'}
          </h2>
          <p className="byod-upsell-blurb">
            {inGrace.length > 0 ? (
              <>Subscribe at your current tier ({tierLabel}) to keep your
              custom domain serving without interruption.</>
            ) : (
              <>Make your website feel really yours. Visitors land on{' '}
              <code>yourclub.co.uk</code> instead of{' '}
              <code>yourclub.{PLATFORM_DOMAIN}</code>. SSL, renewals, and apex
              forwarding are included.</>
            )}
          </p>
          <div className="byod-price-card">
            <div className="byod-price-tier">{tierLabel} plan</div>
            <div className="byod-price-main">
              <span className="byod-price-amount">{monthly}</span>
              <span className="byod-price-period">/month</span>
            </div>
            {setup && (
              <div className="byod-price-setup">
                plus a one-off <strong>{setup}</strong> setup fee
              </div>
            )}
            <ul className="byod-price-feats">
              <li>Free SSL certificate, auto-renewed</li>
              <li>Apex (bare-domain) URL forwarding</li>
              <li>Cancel any time, keeps working until your billing period ends</li>
            </ul>
            <button
              type="button"
              className="btn-primary byod-price-cta"
              onClick={startCheckout}
              disabled={billingLoading}
            >
              {billingLoading ? 'Opening checkout…' : `Subscribe, ${monthly}/mo${setup ? ` + ${setup} setup` : ''}`}
            </button>
            <p className="byod-price-fineprint">
              Or <a href="/upgrade">upgrade to Pro</a> to include Custom Domain free.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const domain = input.trim().toLowerCase();
    if (!domain) {
      setError('Enter the domain you own.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await addDomain(domain, 'byo');
      setDomains(prev => [...prev.filter(d => d.domain !== res.domain.domain), res.domain]);
      setInput('');
    } catch (err: any) {
      setError(err?.message || 'Could not add that domain.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (domain: string) => {
    if (!window.confirm(`Remove ${domain}? Your site at ${domain} will go offline.`)) return;
    try {
      await deleteDomain(domain);
      setDomains(prev => prev.filter(d => d.domain !== domain));
    } catch (err: any) {
      setError(err?.message || 'Could not remove that domain.');
    }
  };

  // Active sub or Pro club, show the wizard. Manage-billing link only
  // appears when there's a real subscription (not for Pro, which is
  // included).
  const hasPaidSub = !!billing?.subscriptionId && billing?.status === 'active';

  return (
    <div className="byod-pane">
      {error && <div className="error-message">{error}</div>}
      {info && <div className="success-message">{info}</div>}

      {hasPaidSub && (
        <div className="byod-billing-strip">
          <span className="byod-billing-info">
            Custom Domain subscription is active
            {billing?.currentPeriodEnd && (
              <>, renews on {new Date(billing.currentPeriodEnd).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</>
            )}
          </span>
          <button
            type="button"
            className="byod-billing-manage"
            onClick={openBillingPortal}
            disabled={billingLoading}
          >
            {billingLoading ? 'Opening…' : 'Manage billing →'}
          </button>
        </div>
      )}

      {loaded && domains.length === 0 && (
        <div className="byod-empty-state">
          <div className="byod-empty-icon" aria-hidden>🌐</div>
          <h2 className="byod-empty-title">Connect your own domain</h2>
          <p className="byod-empty-blurb">
            Make your website feel really yours. Visitors land on
            {' '}<code>yourclub.co.uk</code> instead of{' '}
            <code>yourclub.{PLATFORM_DOMAIN}</code>. We handle SSL,
            renewals, everything.
          </p>
          <form className="byod-add-form" onSubmit={handleAdd}>
            <label htmlFor="byod-domain-input">Domain you own</label>
            <div className="byod-add-row">
              <input
                id="byod-domain-input"
                type="text"
                placeholder="yourclub.co.uk"
                value={input}
                onChange={e => setInput(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                disabled={submitting}
              />
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Adding…' : 'Add domain'}
              </button>
            </div>
            <p className="form-hint">
              Don't own one yet? Buy any <code>.co.uk</code> from Namecheap,
              123-Reg, or GoDaddy (≈ £10/yr) and come back with the name.
            </p>
          </form>
        </div>
      )}

      {domains.map(d => (
        <div key={d.domain} className={`byod-card byod-status-${d.status}`}>
          <div className="byod-card-head">
            <div>
              <h3 className="byod-domain-name">{d.domain}</h3>
              <p className="byod-status-line">
                <span className={`byod-status-dot byod-dot-${d.status}`} />
                {STATUS_LABEL[d.status]}
              </p>
            </div>
            <button
              type="button"
              className="btn-link byod-remove"
              onClick={() => handleRemove(d.domain)}
            >
              Remove
            </button>
          </div>

          <p className="byod-help">{STATUS_HELP[d.status]}</p>

          {(d.status === 'pending' || d.status === 'validating' || d.status === 'failed') && d.validationCnames.length > 0 && (
            <div className="byod-records">
              <p className="byod-records-intro">
                Add the records below at your domain registrar, the same place
                you set up your domain (GoDaddy, 123-Reg, Namecheap, Squarespace,
                Route 53, etc.). All standard CNAME records.
              </p>

              {/* Group records by purpose. Older rows may not have a purpose
                  field set; default-bucket them as 'ssl' so the wizard never
                  misses showing them. */}
              {(() => {
                const sslRecords = d.validationCnames.filter(r => (r.purpose ?? 'ssl') === 'ssl');
                const trafficRecords = d.validationCnames.filter(r => r.purpose === 'traffic');
                return (
                  <>
                    {sslRecords.length > 0 && (
                      <div className="byod-record-group">
                        <h4 className="byod-record-group-title">
                          <span className="byod-record-group-num">1</span>
                          SSL certificate validation
                          <span className="byod-record-group-count">{sslRecords.length} record{sslRecords.length === 1 ? '' : 's'}</span>
                        </h4>
                        <p className="byod-record-group-blurb">
                          One-off, these tell AWS that you own the domain so an SSL
                          certificate can be issued. You can delete them after your
                          domain goes live (AWS keeps a copy for renewals).
                        </p>
                        {sslRecords.map((rec, i) => (
                          <div key={`ssl-${i}`} className="byod-record-pair">
                            <CopyableField label="Type" value={rec.type ?? 'CNAME'} />
                            <CopyableField label="Name" value={rec.name} />
                            <CopyableField label="Value" value={rec.value} />
                          </div>
                        ))}
                      </div>
                    )}
                    {trafficRecords.length > 0 && (
                      <div className="byod-record-group">
                        <h4 className="byod-record-group-title">
                          <span className="byod-record-group-num">2</span>
                          Traffic record
                          <span className="byod-record-group-count">{trafficRecords.length} record</span>
                        </h4>
                        <p className="byod-record-group-blurb">
                          Permanent, points visitors of your <strong>www</strong>
                          subdomain to your site. Don't delete this one.
                        </p>
                        {trafficRecords.map((rec, i) => (
                          <div key={`traffic-${i}`} className="byod-record-pair">
                            <CopyableField label="Type" value={rec.type ?? 'CNAME'} />
                            <CopyableField label="Name" value={rec.name} />
                            <CopyableField label="Value" value={rec.value} />
                          </div>
                        ))}
                      </div>
                    )}
                    {d.apexInstructions && (
                      <div className="byod-record-group byod-record-group-apex">
                        <h4 className="byod-record-group-title">
                          <span className="byod-record-group-num">3</span>
                          Apex setup (bare domain)
                          <span className="byod-record-group-count">registrar setting</span>
                        </h4>
                        <p className="byod-record-group-blurb">
                          The bare domain <code>{d.apexInstructions.source}</code> (without
                          <strong> www</strong>) needs a separate setting because DNS
                          doesn't allow CNAMEs at the apex. Most registrars call this
                          <strong> "URL Forwarding"</strong> or <strong>"Domain Forwarding"</strong>.
                        </p>
                        <div className="byod-apex-instructions">
                          <p>
                            In your registrar, set up a <strong>301 forward</strong> from
                            <code> {d.apexInstructions.source}</code> to
                            <code> {d.apexInstructions.target}</code>.
                          </p>
                          <details>
                            <summary>On an advanced DNS provider (Cloudflare DNS, Route 53, DNSimple)?</summary>
                            <p>
                              You can use an <strong>ALIAS</strong> / <strong>ANAME</strong> /
                              CNAME-flattening record at the apex pointing at the same
                              target as your www CNAME above (or directly at our
                              CloudFront distribution). Same end result.
                            </p>
                          </details>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              <p className="form-hint">
                Once you've added the records, this page will detect them automatically
                within a few minutes, no need to refresh.
              </p>
            </div>
          )}

          {d.status === 'active' && (
            <div className="byod-live">
              <p>
                <a href={`https://www.${d.domain}`} target="_blank" rel="noopener noreferrer">
                  https://www.{d.domain}
                </a>{' '}
                is now serving your website. Update your social bios + tell your members.
              </p>
              <p className="form-hint">
                The bare-domain version (<code>https://{d.domain}</code> without
                <strong> www</strong>) only works if you set up Apex forwarding in
                step 3 above. If you didn't, visitors typing the bare domain will
                see a "site can't be reached" error.
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
